import { createSelector } from '@reduxjs/toolkit';
import _, { chain, clone, orderBy, upperCase } from 'lodash';
import isEmpty from 'lodash/isEmpty';

import { RenewalOrderStatus } from '@appTypes/enums';
import { denIdsSel, isDenOrAssistantSel } from '@context/duck/selectors';
import { programSel } from '@context/duck/selectorsTyped';
import {
  approvedSubUnitsSel,
  subUnitTypeKeysSel,
} from '@modules/advancement/subUnits/duck/selectors';
import { subUnitYouthDictionary } from '@modules/advancement/utilsTyped';
import { rosterApi } from '@modules/roster/api';
import { userIdSel } from '@modules/user/duck/selectors';
import {
  denTypesSingleNames as denNames,
  memberTypes,
  objElementName,
} from '@shared/constants';
import {
  hasNewPermissionSel,
  organizationGuidSel,
  ranksSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';
import { applySort, isIncludedIn } from '@utils';

import {
  packRosterItemsSel as itemsSel,
  packRosterModuleSel,
} from '../../common/selectors';
import { UNASSIGNED } from '../constants';
import {
  filterByRegistrationStatus,
  filterByRenewalStatus,
  filterOutParents,
  isMemberOf,
} from '../filters';
import packRosterSorters from '../packRosterSorters';
import { getPersonRenewalStatus, sortSubUnitsBy } from '../utils';
import { checkHasOrderStatusFilter, getIsDenChief } from '../utilsTyped';
import {
  expandedGroupsSel,
  filterSel,
  loadingPackRosterSel,
  registrationInfoSel,
  searchSel,
  sorterSel,
  subUnitFilterTagsSel,
} from './selectorsTyped';

export {
  selectedKeysSel,
  unitRosterReportLoadingSel,
  isMissingSomeMemberIdSel,
} from '../../common/selectors';
export {
  subUnitTypeNameSel,
  isSubUnitAllowedSel,
} from '../../subUnits/duck/selectors';
export { itemsSel };

const moduleSel = state => packRosterModuleSel(state);

export const loadingSel = state => moduleSel(state).loading;
export const showMemberTypesSel = state => moduleSel(state).showMemberTypes;

export const subUnitsSectionExpandedSel = state =>
  moduleSel(state).subUnitsSectionExpanded;

export const memberTypeSectionExpandedSel = state =>
  moduleSel(state).memberTypeSectionExpanded;

export const subUnitUserDataSel = state => moduleSel(state).subUnitUserData;

export const renewalRosterInfoSel = createSelector(
  moduleSel,
  registrationInfoSel,
  (state, registrationsRTK) => registrationsRTK,
);

export const loadingRenewalSel = createSelector(
  moduleSel,
  loadingPackRosterSel,
  (state, loadingRenewalRTK) => {
    const loadingRenewal = state.loadingRenewal;
    const isLoading = loadingRenewalRTK || loadingRenewal;
    return isLoading;
  },
);

const ordersResponseSel = createSelector(
  [organizationGuidSel, state => state],
  (organizationGuid, state) => {
    const response = rosterApi.endpoints.getUnitOrders.select({
      organizationGuid,
    })(state);

    return response.data;
  },
);

const renewalsByMIDSel = createSelector(ordersResponseSel, response => {
  const ordersByMID = _.keyBy(response?.renewals, 'forMemberId');
  return ordersByMID;
});

export const subUnitsFilteredTagsSel = createSelector(
  subUnitFilterTagsSel,
  isDenOrAssistantSel,
  denIdsSel,
  (subUnitsFilterTags, isDenOrAssistant, denDetails) => {
    if (isDenOrAssistant) {
      const sortedSubUnitsFilterTags = sortSubUnitsBy(
        subUnitsFilterTags,
        objElementName.NAME,
      );
      return sortedSubUnitsFilterTags.filter(subUnitFilterTag =>
        denDetails.includes(subUnitFilterTag.id),
      );
    }
    const sortedSubUnitsFilterTags = sortSubUnitsBy(
      subUnitsFilterTags,
      objElementName.NAME,
    );
    return sortedSubUnitsFilterTags;
  },
);

const isValidPosition = (positionId, subUnits) =>
  subUnits.find(subUnit => subUnit.subUnitId === positionId);

const getSubUnitPosition = (positions, subUnits, keys) => {
  if (positions && subUnits) {
    const positionsByExistingSubUnits = positions.filter(position =>
      isValidPosition(position[keys.id], subUnits),
    );

    const sortedPositions = orderBy(
      positionsByExistingSubUnits,
      'dateStarted',
      'desc',
    );
    const position = sortedPositions.find(
      position => position[keys.id] !== null,
    );
    if (position) {
      const posName = position[keys.name];
      const mappedPosName = denNames[posName]
        ? denNames[posName] === denNames.aol
          ? upperCase(denNames[posName])
          : denNames[posName]
        : posName;
      const denNumber = position[keys.number]
        ? ` ${position[keys.number]}`
        : '';
      return {
        subUnitId: position[keys.id],
        subUnitName: `${mappedPosName}${denNumber}`,
      };
    }
  }
  return { subUnitId: -1, subUnitName: UNASSIGNED };
};

const getSubUnitsList = (
  positions,
  subUnits,
  keys,
  isAdult,
  isDenChief = false,
  hasScoutBSA = false,
) => {
  if (positions && subUnits && (isAdult || isDenChief || hasScoutBSA)) {
    const positionsByExistingSubUnits = positions.filter(position =>
      isValidPosition(position[keys.id], subUnits),
    );

    const isPatrol = positionsByExistingSubUnits.length
      ? !!positionsByExistingSubUnits[0].patrolId
      : false;

    const sortedPositions = chain(positionsByExistingSubUnits)
      .unionBy(isPatrol ? 'patrolId' : 'denId')
      .orderBy('dateStarted', 'dec')
      .value();
    return sortedPositions.length
      ? sortedPositions.map(position => position[keys.id])
      : [];
  }
  return [];
};

export const filteredEnhancedItemsSel = createSelector(
  itemsSel,
  approvedSubUnitsSel,
  subUnitTypeKeysSel,
  isDenOrAssistantSel,
  denIdsSel,
  (items, approvedSubUnits, subUnitTypeKeys, isDenOrAssistant, denIds) => {
    /** @type {import('@modules/advancement/duck/types').RosterScoutWithSubUnit[]} */
    const itemsWithPosition = items.map(item => ({
      ...item,
      ...getSubUnitPosition(
        item.positions || [],
        approvedSubUnits,
        subUnitTypeKeys,
      ),
      subUnitListIds: getSubUnitsList(
        item.positions || [],
        approvedSubUnits,
        subUnitTypeKeys,
        item.isAdult,
        getIsDenChief(item),
        item.positions?.some(
          pos => pos.positionId === subUnitYouthDictionary.SCOUTS_BSA,
        ),
      ),
    }));

    if (isDenOrAssistant && !isEmpty(denIds)) {
      return itemsWithPosition.filter(item => denIds.includes(item.subUnitId));
    }
    return itemsWithPosition;
  },
);

const enhancedItemsSel = createSelector(
  filteredEnhancedItemsSel,
  ranksSel,
  renewalRosterInfoSel,
  state =>
    hasNewPermissionSel(
      state,
      newPermissions.INACTIVE_ADULT_MEMBERS,
      newPermissions.INACTIVE_YOUTH_MEMBERS,
    ),
  (filteredEnhancedItems, ranksList, renewalsInfo, hasOptOutPerms) =>
    filteredEnhancedItems.map(item => {
      const { rosterCurrentHighestRankApproved: rosterRank = {} } = item;
      const renewalStatusFields = getPersonRenewalStatus(
        item,
        renewalsInfo,
        hasOptOutPerms,
        getIsDenChief(item),
      );
      return {
        ...item,
        ...renewalStatusFields,
        rosterCurrentHighestRankApproved: {
          ...rosterRank,
          imageUrl: (ranksList.find(({ id }) => id == rosterRank.id) || {})
            .imageUrl100,
        },
      };
    }),
);

const isIncludedInSubUnitFilters = (scout, filters) => {
  if (filters?.length) {
    return filters.some(
      filter =>
        filter.status &&
        (filter.id === scout.subUnitId ||
          scout.subUnitListIds?.includes(filter.id)),
    );
  }
  return true;
};

const filterRosterItems =
  ({ includeParents }) =>
  (
    items,
    search,
    sorter,
    filter,
    program,
    subUnitsFilteredTags,
    ordersByMID,
  ) => {
    const hasOrderFilter = checkHasOrderStatusFilter(filter);
    const activeOrderStatusFilters = Object.keys(RenewalOrderStatus).filter(
      key => filter[`order_status_${key}`],
    );

    const filteredItems = items.filter(scout => {
      const orderStatus =
        ordersByMID[scout.memberId]?.bsaOrderStatusName ||
        RenewalOrderStatus.NotStarted;

      const orderFilterPass = hasOrderFilter
        ? activeOrderStatusFilters.some(
            activeFilter => orderStatus === RenewalOrderStatus[activeFilter],
          )
        : true;

      const filterResults = {
        byOrderFilter: orderFilterPass,
        byParents: includeParents || filterOutParents(scout),
        byRenewalStatus: filterByRenewalStatus(scout, filter),
        byRegistrationStatus: filterByRegistrationStatus(scout, filter),
        // TODO: extract into individual filters
        byMemberOf: isMemberOf(scout, filter, program),
        bySubUnit: isIncludedInSubUnitFilters(scout, subUnitsFilteredTags),
        bySearchTerm:
          isIncludedIn(scout.personShortFullName, search) ||
          isIncludedIn(scout.personId, search) ||
          isIncludedIn(scout.nickName, search) ||
          isIncludedIn(scout.currentHighestRankApproved.rank, search) ||
          scout.positions?.some(pos => isIncludedIn(pos.position, search)),
      };

      const valid = Object.values(filterResults).every(Boolean);

      return valid;
    });

    const sortedItems = applySort({
      sorter,
      items: filteredItems,
      sorters: packRosterSorters,
    });

    return sortedItems;
  };

const filteredItemsInputSels = [
  enhancedItemsSel,
  searchSel,
  sorterSel,
  filterSel,
  programSel,
  subUnitsFilteredTagsSel,
  renewalsByMIDSel,
];

export const filteredItemsSel = createSelector(
  filteredItemsInputSels,
  filterRosterItems({ includeParents: false }),
);

/**
 * Same as filteredItemsSel but keeps parents. Leaders that are also parents
 * of a unit youth get `isLeader: false` after dedupeMembers, so
 * filteredItemsSel drops them.
 */
export const filteredItemsWithParentsSel = createSelector(
  filteredItemsInputSels,
  filterRosterItems({ includeParents: true }),
);

const isSubUnitVisible = (subUnit, filters) => {
  const filter = filters.find(filter => filter.id === subUnit.subUnitId);
  return filter?.status;
};

const groupItemsBySubUnit = (
  items,
  approvedSubUnits,
  subUnitsSectionExpanded,
  subUnitsFilteredTags,
  //TODO: remove unitUsersRosterData when SBL-6710 is fixed
  unitUsersRosterData,
) => {
  const subUnitSection = (approvedSubUnits || [])
    .filter(subUnit => isSubUnitVisible(subUnit, subUnitsFilteredTags))
    .map(subUnit => ({
      ...subUnit,
      key: subUnit.subUnitId,
      isMainRow: true,
      //TODO: remove membersLength when SBL-6710 is fixed
      membersLength: unitUsersRosterData.filter(user =>
        user.subUnitListIds.includes(subUnit.subUnitId),
      ).length,
      expanded: subUnitsSectionExpanded[subUnit.subUnitId],
      subRows: items
        .filter(item => {
          const { subUnitId, subUnitListIds, isAdult, positions = [] } = item;
          const hasScoutBSA = positions.some(
            pos => pos.positionId === subUnitYouthDictionary.SCOUTS_BSA,
          );
          if (
            (isAdult || getIsDenChief(item) || hasScoutBSA) &&
            subUnitListIds.length
          ) {
            return subUnitListIds.some(itemId => itemId === subUnit.subUnitId);
          }
          return subUnitId === subUnit.subUnitId;
        })
        .map(item => {
          const { isAdult, positions = [] } = item;
          const hasScoutBSA = positions.some(
            pos => pos.positionId === subUnitYouthDictionary.SCOUTS_BSA,
          );
          if (isAdult || getIsDenChief(item) || hasScoutBSA) {
            const currentSubUnitId = item.subUnitListIds.find(
              itemId => itemId === subUnit.subUnitId,
            );
            if (currentSubUnitId) {
              const clonedItem = clone(item);
              clonedItem.subUnitId = currentSubUnitId;
              const mappedSubUnitName = denNames[subUnit?.denType]
                ? `${denNames[subUnit?.denType]} ${subUnit?.subUnitNameRaw}`
                : subUnit.subUnitName;
              clonedItem.subUnitName = mappedSubUnitName;
              return clonedItem;
            }
          }

          return item;
        }),
    }));
  const nonSubUnitSection = {
    key: UNASSIGNED,
    isMainRow: true,
    expanded: subUnitsSectionExpanded[UNASSIGNED],
    subRows: items.filter(({ subUnitId }) => subUnitId === -1),
  };

  const sortedSubUnitSection = sortSubUnitsBy(
    subUnitSection,
    objElementName.SUB_UNIT_NAME,
  );

  const unassignedFilter = subUnitsFilteredTags?.find(
    filter => filter.id === -1,
  );

  const completeSubUnitSection = unassignedFilter?.status
    ? [...sortedSubUnitSection, nonSubUnitSection]
    : [...sortedSubUnitSection];

  return completeSubUnitSection;
};

const itemsPerSubUnitInputSels = [
  approvedSubUnitsSel,
  subUnitsSectionExpandedSel,
  subUnitsFilteredTagsSel,
  enhancedItemsSel,
];

export const itemsPerSubUnitSel = createSelector(
  [filteredItemsSel, ...itemsPerSubUnitInputSels],
  groupItemsBySubUnit,
);

/**
 * Same as itemsPerSubUnitSel but includes adults that are also parents
 * (e.g. den leaders with a youth in the unit).
 */
export const itemsPerSubUnitWithParentsSel = createSelector(
  [filteredItemsWithParentsSel, ...itemsPerSubUnitInputSels],
  groupItemsBySubUnit,
);

export const itemsPerMemberTypeSel = createSelector(
  filteredItemsSel,
  memberTypeSectionExpandedSel,
  (items, memberTypeSectionExpanded) => {
    const memberTypeSection = memberTypes.map(memberType => {
      const subRows = items.filter(
        ({ memberTypeId }) => memberTypeId === memberType.id,
      );
      return {
        key: memberType.label,
        name: memberType.label,
        itemsCount: subRows.length,
        isMainRow: true,
        expanded: memberTypeSectionExpanded[memberType.label],
        subRows,
      };
    });
    return memberTypeSection;
  },
);
export const itemsPerOrderStatusSel = createSelector(
  ordersResponseSel,
  renewalsByMIDSel,
  filteredItemsSel,
  expandedGroupsSel,
  (response, ordersByMID, filteredItems, expandedGroups) => {
    const groupedByOrderStatus = _.chain(filteredItems)
      .groupBy(item => {
        const mid = item.memberId;
        return (
          ordersByMID[`${mid}`]?.bsaOrderStatusName ||
          RenewalOrderStatus.NotStarted
        );
      })
      .value();

    const groups = Object.entries(RenewalOrderStatus)
      .map(([statusKey, label]) => {
        const subRows =
          groupedByOrderStatus[RenewalOrderStatus[statusKey]] || [];
        const expanded = !!expandedGroups[`order-status:${statusKey}`];
        return {
          key: statusKey,
          groupId: statusKey,
          label,
          itemsCount: subRows.length,
          isMainRow: true,
          subRows,
          expanded,
        };
      })
      .filter(group => group.itemsCount > 0);

    return groups;
  },
);

export const currentUserSubunitsSel = createSelector(
  itemsSel,
  userIdSel,
  state =>
    hasNewPermissionSel(
      state,
      newPermissions.EDIT_SUB_PROFILE,
      newPermissions.APPROVE_ADVANCEMENTS,
      newPermissions.EDIT_ADVANCEMENTS,
      newPermissions.EDIT_SUB_UNIT,
    ),
  (items, userId, canEditSubProfiles) => {
    const hasSubUnitOnlyPerms = canEditSubProfiles;
    // We don't need to know the user subunits if they have full access to the unit
    if (!hasSubUnitOnlyPerms) return [];

    const foundInRoster = items.find(item => item.userId === userId);
    if (foundInRoster) {
      const { positions = [] } = foundInRoster;
      return positions.filter(pos => pos.denId || pos.patrolId);
    }

    return [];
  },
);

export const userSubUnitMembersSel = createSelector(
  itemsSel,
  currentUserSubunitsSel,
  (roster, userSubUnits) => {
    /**
     * @type {import('@modules/advancement/duck/types').RosterScout[]}
     */
    const filteredMembers = roster.filter(member => {
      const belongsToSubUnit = member.positions?.some(pos =>
        userSubUnits.some(mySub => {
          const mySubId = mySub.patrolId || mySub.denId;
          const posSubId = pos.patrolId || pos.denId;
          return mySubId === posSubId;
        }),
      );
      return belongsToSubUnit;
    });
    return filteredMembers;
  },
);
